"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import {
	Alert,
	AmountSlider,
	Button,
	Checkbox,
	Stack,
} from "@leabankab/component-library";
import { FormField } from "@leabankab/component-library/react-hook-form";
import { StepContainer } from "@/components/layout";
import { StepErrorMessage } from "@/components/ui/step-error-message";
import {
	ApplicationProvider,
	useOptionalApplication,
} from "@/contexts/application-context";
import { useFormContext } from "@/contexts/form";
import { useRuntimeConfig } from "@/contexts/runtime-config";
import { StepNavigationProvider } from "@/contexts/step-navigation-context";
import { DevFlowControls } from "@/dev/console/dev-flow-controls";
import { LoanDisclaimer } from "@/features/landing/components/loan-disclaimer";
import { RepaymentPeriodSelector } from "@/features/landing/components/repayment-period-selector";
import { useCreateApplication } from "@/features/landing/use-create-application";
import { LandingPageForm } from "@/features/landing/use-landing-page-form";
import { getLandingAttribution } from "@/features/landing/utils/get-landing-attribution";
import { useCountrySettings } from "@/hooks/use-app-country";
import { useAdvanceLanding } from "@/hooks/use-save-form-to-cache";
import { StepType } from "@/types/routes";

interface Props {
	/**
	 * Create throws on failure, update resolves `false` — either way the page
	 * has to stay put and say so.
	 */
	onSubmit: (landingPageForm: LandingPageForm) => Promise<boolean | void>;
}

const LandingPageContent = ({ onSubmit }: Props) => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string>();
	const t = useTranslations("pages.LandingPage");
	const tErrors = useTranslations("forms.errors.general");
	const dateUnits = useTranslations("units.dates");

	const {
		control: landingControl,
		formState: { isValid: isLandingValid },
		getValues,
		setValue,
	} = useFormContext("landingPageForm");

	const { control: aboutControl, watch: watchAbout } =
		useFormContext("aboutForm");

	const appliedAmount = watchAbout("appliedAmount");
	const appliedRepaymentMonths = watchAbout("appliedRepaymentTime");
	const valueYears = Math.round((appliedRepaymentMonths ?? 120) / 12);
	const { loanAmount: loanAmountLimits, landingDebtWarning } =
		useCountrySettings();

	const policyCheckboxLabel = t.rich("checkbox.terms", {
		terms: (chunks) => (
			<Link
				href={t.raw("checkboxLinks.terms")}
				target="_blank"
				rel="noopener noreferrer"
				style={{ fontWeight: 400, textDecoration: "underline" }}
			>
				{chunks}
			</Link>
		),
		secci: (chunks) => (
			<Link
				href={t.raw("checkboxLinks.secci")}
				target="_blank"
				rel="noopener noreferrer"
				style={{ fontWeight: 400, textDecoration: "underline" }}
			>
				{chunks}
			</Link>
		),
		privacy: (chunks) => (
			<Link
				href={t.raw("checkboxLinks.privacy")}
				target="_blank"
				rel="noopener noreferrer"
				style={{ fontWeight: 400, textDecoration: "underline" }}
			>
				{chunks}
			</Link>
		),
	});

	const canContinue =
		isLandingValid &&
		(appliedAmount ?? 0) >= loanAmountLimits.min &&
		(appliedAmount ?? 0) <= loanAmountLimits.max &&
		(appliedRepaymentMonths ?? 0) > 0;

	return (
		<StepContainer>
			<Stack gap={4}>
				<FormField
					control={aboutControl}
					name="appliedAmount"
					render={({ field }) => (
						<AmountSlider
							label={t("loanAmount.label")}
							min={loanAmountLimits.min}
							max={loanAmountLimits.max}
							step={loanAmountLimits.step}
							value={field.value ?? loanAmountLimits.default}
							onValueChange={field.onValueChange}
						/>
					)}
				/>

				<FormField
					control={aboutControl}
					name="appliedRepaymentTime"
					render={({ field }) => (
						<Stack gap={1.5}>
							<RepaymentPeriodSelector
								label={t("repayment.label")}
								valueYears={valueYears}
								onChange={(years) =>
									field.onValueChange(years * 12)
								}
								yearLabel={(years) =>
									dateUnits("years", { years: years ?? 0 })
								}
							/>
							<Alert color="primary" size="sm">
								{t("repayment.info")}
							</Alert>
						</Stack>
					)}
				/>

				<Stack gap={0.5}>
					<FormField
						control={landingControl}
						name="isAffirmedIntegrityPolicyCheckbox"
						render={({ field }) => (
							<Checkbox
								label={policyCheckboxLabel}
								checked={field.value ?? false}
								onCheckedChange={field.onValueChange}
							/>
						)}
					/>
					<FormField
						control={landingControl}
						name="isAffirmedMarketingCommunicationCheckbox"
						render={({ field }) => (
							<Checkbox
								label={t("checkbox.marketing")}
								checked={field.value ?? false}
								onCheckedChange={field.onValueChange}
							/>
						)}
					/>
				</Stack>

				<Button
					disabled={!canContinue || isLoading}
					onClick={async () => {
						setIsLoading(true);
						const attribution = getLandingAttribution(
							window.location.search
						);
						if (attribution.campaignId) {
							setValue("campaignId", attribution.campaignId, {
								shouldDirty: true,
							});
						}
						if (attribution.utmCampaign) {
							setValue("utmCampaign", attribution.utmCampaign, {
								shouldDirty: true,
							});
						}
						if (attribution.utmSource) {
							setValue("utmSource", attribution.utmSource, {
								shouldDirty: true,
							});
						}
						if (attribution.utmMedium) {
							setValue("utmMedium", attribution.utmMedium, {
								shouldDirty: true,
							});
						}
						if (attribution.utmTerm) {
							setValue("utmTerm", attribution.utmTerm, {
								shouldDirty: true,
							});
						}
						if (attribution.utmContent) {
							setValue("utmContent", attribution.utmContent, {
								shouldDirty: true,
							});
						}
						if (attribution.adtractionId) {
							setValue("adtractionId", attribution.adtractionId, {
								shouldDirty: true,
							});
						}
						if (attribution.googleClickId) {
							setValue(
								"googleClickId",
								attribution.googleClickId,
								{
									shouldDirty: true,
								}
							);
						}
						if (attribution.facebookClickId) {
							setValue(
								"facebookClickId",
								attribution.facebookClickId,
								{
									shouldDirty: true,
								}
							);
						}
						try {
							const saved = await onSubmit({
								isAffirmedIntegrityPolicyCheckbox: getValues(
									"isAffirmedIntegrityPolicyCheckbox"
								),
								isAffirmedMarketingCommunicationCheckbox:
									getValues(
										"isAffirmedMarketingCommunicationCheckbox"
									),
								...attribution,
							});

							if (saved === false) {
								setError(tErrors("saveFailed"));
								setIsLoading(false);
							}
						} catch (err) {
							console.error(err);
							setError(tErrors("saveFailed"));
							setIsLoading(false);
						}
					}}
					loading={isLoading}
				>
					{isLoading ? t("buttonLoadingText") : t("buttonText")}
				</Button>

				<StepErrorMessage>{error}</StepErrorMessage>

				{landingDebtWarning && (
					<LoanDisclaimer title={t("disclaimer.title")}>
						{t.rich("disclaimer.body", {
							link: (chunks) => (
								<Link
									href={t.raw("disclaimerLink")}
									target="_blank"
									rel="noopener noreferrer"
									style={{
										fontWeight: 400,
										textDecoration: "underline",
									}}
								>
									{chunks}
								</Link>
							),
						})}
					</LoanDisclaimer>
				)}
			</Stack>
		</StepContainer>
	);
};

const CreateLandingPage = () => {
	const createApplication = useCreateApplication();
	const { devConsole } = useRuntimeConfig();
	return (
		<>
			<LandingPageContent onSubmit={createApplication} />
			{devConsole && (
				<ApplicationProvider value={{ applicationId: "" }}>
					<StepNavigationProvider initialStep={StepType.Landing}>
						<DevFlowControls />
					</StepNavigationProvider>
				</ApplicationProvider>
			)}
		</>
	);
};

const UpdateLandingPage = () => {
	const saveFormToCache = useAdvanceLanding();
	return <LandingPageContent onSubmit={() => saveFormToCache()} />;
};

/**
 * The landing page is rendered both before an application exists (create, at
 * `/`) and as the first step within an application flow (update,
 * inside `StepRouter`). It self-selects the submit behaviour based on whether
 * an `ApplicationProvider` is present.
 */
const LandingPage = () => {
	const application = useOptionalApplication();
	return application?.applicationId ? (
		<UpdateLandingPage />
	) : (
		<CreateLandingPage />
	);
};

export default LandingPage;
